import { Input, useAction } from '@agro-club/frontend-shared'
import { useFormik } from 'formik'
import useLangPicker from 'hooks/useLangPicker'
import useValidationErrorNotification from 'hooks/useValidationErrorNotification'
import AuthSelectors from 'modules/domain/auth/selectors'
import { PedigreedGrowerTargetSku, TargetSkuTypes } from 'modules/domain/target2sku/common/types'
import PedigreedGrowerTargetSkuActions from 'modules/domain/target2sku/pedigreedGrowerTarget/duck'
import PedigreedGrowerTargetSkuSelectors from 'modules/domain/target2sku/pedigreedGrowerTarget/selectors'
import { Progress } from 'modules/types'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { isAgro, isProducer } from 'types/entities'
import { CompoundedTableEditableForm } from 'views/components/CompoundedTable/CompoundedTableEditable/CompoundedTableEditableRow'
import * as Yup from 'yup'
import { TargetSkuGrowerSelect } from '../selects/TargetSkuGrowerSelect'
import { TargetSkuManufacturerSelect } from '../selects/TargetSkuManufacturerSelect'
import { TargetSkuProductSelect } from '../selects/TargetSkuProductSelect'
import { TargetSkuSubcategorySelect } from '../selects/TargetSkuSubcategorySelect'
import { TargetSkuTerritorySelect } from '../selects/TargetSkuTerritorySelect'
import { FormWrapper } from '../styles'
import { usePedigreedTargetValueSchema } from './usePedigreedTargetValueSchema'

export const PedigreedGrowerTargetSkuForm: CompoundedTableEditableForm<PedigreedGrowerTargetSku> = ({
  Buttons,
  entity,
}) => {
  const { t } = useTranslation(['target2', 'validation'])

  const addRequested = useAction(PedigreedGrowerTargetSkuActions.addRequested)
  const addProgress = useSelector(PedigreedGrowerTargetSkuSelectors.addProgress)
  const addErrorDetail = useSelector(PedigreedGrowerTargetSkuSelectors.addErrorDetail)

  const removeRequested = useAction(PedigreedGrowerTargetSkuActions.removeRequested)
  const removeProgress = useSelector(PedigreedGrowerTargetSkuSelectors.removeProgress)
  const removeErrorDetail = useSelector(PedigreedGrowerTargetSkuSelectors.removeErrorDetail)

  const updateProgress = useSelector(PedigreedGrowerTargetSkuSelectors.updateProgress)
  const meta = useSelector(PedigreedGrowerTargetSkuSelectors.meta(entity?.id))

  const updateRequested = useAction(PedigreedGrowerTargetSkuActions.updateRequested)
  const userCompany = useSelector(AuthSelectors.userCompany)

  const role = useSelector(AuthSelectors.role)
  const producer = isProducer(role)
  const agro = isAgro(role)

  const { pick } = useLangPicker()

  const targetValue = usePedigreedTargetValueSchema()
  const validationSchema = useMemo(() => {
    return entity
      ? Yup.object({
          target_value: targetValue,
        })
      : Yup.object({
          territory_id: Yup.string().required(t('validation:field_required')),
          grower_id: Yup.string().required(t('validation:field_required')),
          manufacturer_id: Yup.string().required(t('validation:field_required')),
          product_id: Yup.string().required(t('validation:field_required')),
          target_value: targetValue,
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, targetValue, !!entity])

  // This is to prevent reinitialize on every render
  const initialValues = useMemo(
    () => ({
      territory_id: '',
      grower_id: '',
      manufacturer_id: userCompany && producer ? userCompany.id : '',
      product_id: '',
      target_value: entity?.target_value ?? 0,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity?.target_value],
  )

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (entity?.id) {
        updateRequested(entity.id, { target_value: values.target_value })
      } else {
        addRequested(values, false)
      }
    },
  })

  const isEdit = !!entity

  useEffect(() => {
    if (addProgress === Progress.SUCCESS && !entity) {
      formik.resetForm()
      setSubcategoryId('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addProgress])

  // Reset form on update success
  useEffect(() => {
    formik.resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.target_value])

  const [subcategoryId, setSubcategoryId] = useState<string | undefined>('')

  useValidationErrorNotification(
    formik.submitCount,
    formik.isValid,
    removeErrorDetail || entity ? meta.updateErrorDetail : addErrorDetail,
  )

  const handleChangeManufacturer = (val) => {
    formik.setFieldValue('manufacturer_id', val)
    formik.setFieldValue('territory_id', '')
    formik.setFieldValue('grower_id', '')
    formik.setFieldValue('product_id', '')
    setSubcategoryId('')
  }

  return (
    <>
      <FormWrapper>
        {agro && (
          <TargetSkuManufacturerSelect
            required
            label={t('form.labels.manufacturer')}
            placeholder=""
            isDisabled={isEdit}
            value={formik.values.manufacturer_id}
            valueLabel={entity?.manufacturer?.title}
            onChange={handleChangeManufacturer}
            invalid={formik.touched.manufacturer_id && !!formik.errors.manufacturer_id}
          />
        )}
        <TargetSkuTerritorySelect
          required
          placeholder=""
          label={t('form.labels.territory')}
          isDisabled={isEdit}
          value={formik.values.territory_id}
          valueLabel={entity?.territory?.title}
          onChange={(val) => {
            formik.setFieldValue('territory_id', val)
            formik.setFieldValue('grower_id', '')
            formik.setFieldValue('product_id', '')
            setSubcategoryId('')
          }}
          invalid={formik.touched.territory_id && !!formik.errors.territory_id}
          scope="pedigreed"
        />
        <TargetSkuGrowerSelect
          required
          placeholder=""
          label={t('form.labels.grower')}
          isDisabled={isEdit || !formik.values.territory_id}
          value={formik.values.grower_id}
          valueLabel={entity?.grower?.title}
          onChange={(val) => {
            formik.setFieldValue('grower_id', val)
          }}
          invalid={!!formik.submitCount && !!formik.errors.grower_id}
          territoryId={formik.values.territory_id}
        />
        {!isEdit && (
          <TargetSkuSubcategorySelect
            label={t('form.labels.subcategory')}
            placeholder=""
            value={subcategoryId}
            onChange={(val) => {
              setSubcategoryId(val)
              formik.setFieldValue('product_id', '')
            }}
            manufacturerId={formik.values.manufacturer_id}
            territoryId={formik.values.territory_id}
            onlyCurrentSeason
            isClearable
            isDisabled={!formik.values.manufacturer_id || !formik.values.territory_id}
            scope={TargetSkuTypes.Pedigreed}
          />
        )}
        <TargetSkuProductSelect
          required
          placeholder=""
          label={t('form.labels.product')}
          isDisabled={isEdit || !formik.values.manufacturer_id || !formik.values.territory_id}
          value={formik.values.product_id}
          valueLabel={pick(entity?.product?.title)}
          onChange={(val) => {
            formik.setFieldValue('product_id', val)
          }}
          invalid={formik.touched.product_id && !!formik.errors.product_id}
          subcategoryId={subcategoryId}
          manufacturerId={formik.values.manufacturer_id}
          territoryId={formik.values.territory_id}
          onlyCurrentSeason
          scope={TargetSkuTypes.Pedigreed}
        />
        <Input
          required
          label={t('form.labels.target')}
          {...formik.getFieldProps('target_value')}
          invalid={formik.touched.target_value && !!formik.errors.target_value}
          type="number"
          min={0}
        />
      </FormWrapper>
      <Buttons
        onSubmit={formik.handleSubmit}
        onRemove={entity ? () => removeRequested(entity.id) : undefined}
        submitProgress={isEdit ? updateProgress : addProgress}
        removeProgress={removeProgress}
        createText={t('form.create')}
        removeText={t('form.delete')}
        updateText={t('form.update')}
        isSubmitDisabled={!formik.dirty}
      />
    </>
  )
}
